import React, { useEffect, useMemo, useState } from 'react';
import { Card } from '../../components/ui/Card';
import { useAuth } from '../../context/AuthContext';
import { Calendar, RefreshCw, Eye, ShoppingCart, CreditCard, TrendingUp, Clock, FileText, Timer } from 'lucide-react';

const API_BASE = 'https://api.glowytics.com';

const DATE_OPTIONS = [
  { value: 'last30', label: 'Last 30 days' },
  { value: 'last90', label: 'Last 90 days' },
  { value: 'thisMonth', label: 'This month' },
  { value: 'lastMonth', label: 'Last month' },
  { value: 'custom', label: 'Custom range' },
];

const fmtNumber = (n) => Number(n || 0).toLocaleString('tr-TR');
const fmtFloat = (n, digits = 2) => Number(n || 0).toLocaleString('tr-TR', { minimumFractionDigits: digits, maximumFractionDigits: digits });
const fmtPercent = (n, digits = 2) => `${fmtFloat(Number(n || 0) * 100, digits)}%`;
const fmtDuration = (sec) => {
  const s = Math.round(Number(sec || 0));
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}m ${r}s`;
};

const formatMonthLabel = (key) => {
  if (!key || key.length < 7) return key || '';
  const [y, m] = key.split('-');
  const d = new Date(Number(y), Number(m) - 1, 1);
  return d.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
};

export default function SessionEngagement() {
  const { selectedBrand } = useAuth();
  const [viewMode, setViewMode] = useState('monthly'); // 'monthly' | 'summary'
  const [dateRange, setDateRange] = useState('last30');
  const [customDates, setCustomDates] = useState({ startDate: '', endDate: '' });
  // PDP URL pattern — case-insensitive `contains` filter on pagePath.
  // Default `-p-` works for high5 and most Turkish e-commerce slugs
  // (e.g. /on-cloudmonster-p-44523). User can override per brand.
  const [pdpPattern, setPdpPattern] = useState('-p-');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [payload, setPayload] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);

  const isMonthly = viewMode === 'monthly';

  const rows = useMemo(() => {
    const list = Array.isArray(payload?.data) ? payload.data : [];
    if (!isMonthly) return list;
    return [...list].sort((a, b) => (a.month < b.month ? 1 : -1)); // newest first
  }, [payload, isMonthly]);

  const totals = payload?.totals || {
    sessions: 0, itemViews: 0, addToCarts: 0, checkouts: 0, purchases: 0,
    itemsViewedInList: 0, avgSessionDuration: 0, pagesPerSession: 0,
    viewsPerSession: 0, atcPerView: 0, checkoutsPerSession: 0, cr: 0,
    itemsListPerSession: 0, pdpEngagementSec: 0, pdpPageViews: 0, avgTimeOnPdp: 0,
    pdpSessions: 0, pdpAvgSessionDuration: 0,
  };

  const fetchData = async () => {
    if (!selectedBrand?.id) return;
    if (!isMonthly && dateRange === 'custom' && (!customDates.startDate || !customDates.endDate)) {
      setError('Please select a start and end date.');
      return;
    }
    try {
      setLoading(true);
      setError('');
      const url = new URL(`${API_BASE}/api/google-analytics/ga4/per-session-engagement`);
      if (isMonthly) {
        url.searchParams.set('granularity', 'monthly');
        url.searchParams.set('months', '6');
      } else {
        url.searchParams.set('granularity', 'total');
        url.searchParams.set('dateRange', dateRange);
        if (dateRange === 'custom') {
          url.searchParams.set('startDate', customDates.startDate);
          url.searchParams.set('endDate', customDates.endDate);
        }
      }
      if (pdpPattern && pdpPattern.trim()) {
        url.searchParams.set('pdpPattern', pdpPattern.trim());
      }
      const res = await fetch(url.toString(), {
        method: 'GET',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', 'X-Brand-ID': selectedBrand.id },
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data?.error || data?.details || `HTTP ${res.status}`);
      }
      const data = await res.json().catch(() => ({}));
      setPayload(data);
      setLastUpdated(new Date());
    } catch (e) {
      setPayload(null);
      setError(e?.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBrand?.id, dateRange, customDates.startDate, customDates.endDate, viewMode, pdpPattern]);

  // Cards shown above the table — for monthly view they show aggregated totals
  const summaryCards = [
    {
      label: 'Sessions',
      value: fmtNumber(totals.sessions),
      sub: isMonthly ? 'Last 6 months' : 'Selected period',
      icon: TrendingUp,
      color: 'text-blue-600 bg-blue-50',
    },
    {
      label: 'Product Views / Session',
      value: fmtFloat(totals.viewsPerSession),
      sub: `${fmtNumber(totals.itemViews)} Product View events`,
      icon: Eye,
      color: 'text-violet-600 bg-violet-50',
    },
    {
      label: 'Add to Cart / Product View',
      value: fmtPercent(totals.atcPerView),
      sub: `${fmtNumber(totals.addToCarts)} ATC / ${fmtNumber(totals.itemViews)} PDP view`,
      icon: ShoppingCart,
      color: 'text-amber-600 bg-amber-50',
    },
    {
      label: 'Begin Checkout / Session',
      value: fmtPercent(totals.checkoutsPerSession),
      sub: `${fmtNumber(totals.checkouts)} checkout starts`,
      icon: CreditCard,
      color: 'text-orange-600 bg-orange-50',
    },
    {
      label: 'Purchase Rate (CR)',
      value: fmtPercent(totals.cr),
      sub: `${fmtNumber(totals.purchases)} purchases`,
      icon: TrendingUp,
      color: 'text-emerald-600 bg-emerald-50',
    },
    {
      label: 'Pages / Session',
      value: fmtFloat(totals.pagesPerSession),
      sub: 'Site engagement depth',
      icon: FileText,
      color: 'text-indigo-600 bg-indigo-50',
    },
    {
      label: 'Avg Session Duration',
      value: fmtDuration(totals.avgSessionDuration),
      sub: 'Time on site (mean)',
      icon: Clock,
      color: 'text-rose-600 bg-rose-50',
    },
    {
      label: 'Avg PDP Time',
      value: fmtDuration(totals.avgTimeOnPdp),
      sub: `${fmtNumber(totals.pdpPageViews)} PDP views · pattern "${pdpPattern}"`,
      icon: Timer,
      color: 'text-pink-600 bg-pink-50',
    },
    {
      label: 'Avg Duration (PDP Sessions)',
      value: fmtDuration(totals.pdpAvgSessionDuration),
      sub: `${fmtNumber(totals.pdpSessions)} session with view_item`,
      icon: Clock,
      color: 'text-fuchsia-600 bg-fuchsia-50',
    },
  ];

  return (
    <div className="p-6 space-y-6">
      <Card className="rounded-2xl overflow-hidden shadow-xl border border-gray-100">
        {/* Header */}
        <div className="p-6 bg-white text-gray-900 border-b">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
            <div className="flex items-center gap-4">
              <img src="/GA4logoicon.svg" alt="GA4" className="w-12 h-12 object-contain" />
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Session Engagement</h1>
                <p className="text-sm text-gray-500 mt-1">
                  Session başına ortalama metrik'ler — kaç ürün detayı görüntüleniyor, kaç sepete ekleme oluyor, satın alma oranı (CR). Canlı GA4 Data API.
                </p>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <div className="flex items-center bg-gray-100 rounded-lg p-1">
                <button
                  type="button"
                  onClick={() => setViewMode('summary')}
                  className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                    viewMode === 'summary'
                      ? 'bg-white text-blue-600 shadow-sm'
                      : 'text-gray-600 hover:text-gray-800'
                  }`}
                >
                  Summary
                </button>
                <button
                  type="button"
                  onClick={() => setViewMode('monthly')}
                  className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                    viewMode === 'monthly'
                      ? 'bg-white text-blue-600 shadow-sm'
                      : 'text-gray-600 hover:text-gray-800'
                  }`}
                >
                  Monthly (6M)
                </button>
              </div>
              {!isMonthly && (
                <div className="relative">
                  <select
                    value={dateRange}
                    onChange={(e) => setDateRange(e.target.value)}
                    className="pl-9 pr-4 py-2 bg-white border rounded-xl text-sm appearance-none focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {DATE_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                  <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                </div>
              )}
              {!isMonthly && dateRange === 'custom' && (
                <>
                  <input
                    type="date"
                    value={customDates.startDate}
                    onChange={(e) => setCustomDates((prev) => ({ ...prev, startDate: e.target.value }))}
                    className="px-3 py-2 text-sm border rounded-lg"
                  />
                  <input
                    type="date"
                    value={customDates.endDate}
                    onChange={(e) => setCustomDates((prev) => ({ ...prev, endDate: e.target.value }))}
                    className="px-3 py-2 text-sm border rounded-lg"
                  />
                </>
              )}
              <div className="flex items-center gap-1.5 bg-white border rounded-xl px-2.5 py-1">
                <Timer className="w-3.5 h-3.5 text-pink-600" />
                <span className="text-[11px] text-gray-500">PDP URL pattern:</span>
                <input
                  type="text"
                  value={pdpPattern}
                  onChange={(e) => setPdpPattern(e.target.value)}
                  placeholder="-p-"
                  className="w-20 text-xs font-medium text-gray-800 focus:outline-none"
                  title="Page path bu metni içeren sayfalar PDP olarak kabul edilir (case-insensitive)"
                />
              </div>
              <button
                type="button"
                onClick={fetchData}
                className="p-2 text-gray-600 hover:text-blue-600 rounded-xl hover:bg-blue-50 transition-colors"
                disabled={loading}
                title="Refresh"
              >
                <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
              </button>
            </div>
          </div>
          {lastUpdated && (
            <p className="mt-3 text-[11px] text-gray-400">
              Son güncelleme: {lastUpdated.toLocaleTimeString('tr-TR')} ·
              {payload?.startDate && payload?.endDate ? ` ${payload.startDate} → ${payload.endDate}` : ''}
            </p>
          )}
        </div>

        {/* Body */}
        {loading ? (
          <div className="px-6 py-16 bg-white text-center text-gray-500">Loading session engagement…</div>
        ) : error ? (
          <div className="px-6 py-16 bg-white text-center text-red-600">{error}</div>
        ) : (rows.length === 0) ? (
          <div className="px-6 py-16 bg-white text-center text-gray-500">No data for this period.</div>
        ) : (
          <div className="p-6 bg-white space-y-6">
            {/* Summary cards */}
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              {summaryCards.map((c) => (
                <div key={c.label} className="rounded-xl border border-gray-100 bg-white p-4 shadow-sm">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="text-[11px] uppercase text-gray-500 tracking-wide">{c.label}</div>
                      <div className="mt-2 text-xl font-semibold text-gray-900 tabular-nums">{c.value}</div>
                      <div className="mt-1 text-[11px] text-gray-500">{c.sub}</div>
                    </div>
                    <div className={`p-2 rounded-lg ${c.color}`}>
                      <c.icon className="w-4 h-4" />
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Monthly table */}
            {isMonthly && rows.length > 0 && (
              <div className="overflow-x-auto rounded-xl border border-gray-100">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 text-gray-600">
                    <tr>
                      <Th>Month</Th>
                      <Th align="right">Sessions</Th>
                      <Th align="right" title="view_item event count (raw)">Product Views</Th>
                      <Th align="right" title="view_item events / sessions">Product Views / Session</Th>
                      <Th align="right" title="add_to_cart event count (raw)">Add to Cart</Th>
                      <Th align="right" title="add_to_cart events / view_item events — PDP→ATC dönüşüm oranı">Add to Cart / Product View</Th>
                      <Th align="right" title="begin_checkout events / sessions">Checkout / Session</Th>
                      <Th align="right" title="purchases / sessions">CR</Th>
                      <Th align="right">Pages / Session</Th>
                      <Th align="right">Avg Duration</Th>
                      <Th align="right" title="averageSessionDuration filtered to sessions where view_item event fired">Avg Duration (PDP Sessions)</Th>
                      <Th align="right" title={`PDP üzerinde ortalama geçirilen süre (userEngagementDuration / screenPageViews, pagePath contains "${pdpPattern}")`}>Avg PDP Time</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r) => (
                      <tr key={r.month} className="border-t border-gray-100 hover:bg-gray-50/60">
                        <Td className="font-medium text-gray-800">{formatMonthLabel(r.month)}</Td>
                        <Td align="right" className="tabular-nums">{fmtNumber(r.sessions)}</Td>
                        <Td align="right" className="tabular-nums text-violet-700 font-semibold">{fmtNumber(r.itemViews)}</Td>
                        <Td align="right" className="tabular-nums text-violet-700">{fmtFloat(r.viewsPerSession)}</Td>
                        <Td align="right" className="tabular-nums text-amber-700 font-semibold">{fmtNumber(r.addToCarts)}</Td>
                        <Td align="right" className="tabular-nums text-amber-700">{fmtPercent(r.atcPerView)}</Td>
                        <Td align="right" className="tabular-nums text-orange-700">{fmtPercent(r.checkoutsPerSession)}</Td>
                        <Td align="right" className="tabular-nums text-emerald-700 font-semibold">{fmtPercent(r.cr)}</Td>
                        <Td align="right" className="tabular-nums">{fmtFloat(r.pagesPerSession)}</Td>
                        <Td align="right" className="tabular-nums">{fmtDuration(r.avgSessionDuration)}</Td>
                        <Td align="right" className="tabular-nums text-fuchsia-700 font-semibold">{fmtDuration(r.pdpAvgSessionDuration)}</Td>
                        <Td align="right" className="tabular-nums text-pink-700 font-semibold">{fmtDuration(r.avgTimeOnPdp)}</Td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

          </div>
        )}
      </Card>
    </div>
  );
}

function Th({ children, align = 'left', title }) {
  return (
    <th
      title={title}
      className={`px-3 py-2 text-[11px] font-semibold uppercase tracking-wide ${align === 'right' ? 'text-right' : 'text-left'}`}
    >
      {children}
    </th>
  );
}

function Td({ children, align = 'left', className = '' }) {
  return (
    <td className={`px-3 py-2 ${align === 'right' ? 'text-right' : 'text-left'} ${className}`}>
      {children}
    </td>
  );
}
